/**
 * Indicadores (`/api/indicadores`).
 *
 * Un indicador vive EN UNA VERSIÓN y se ancla a un aspecto del árbol. Un aspecto
 * está activo en una versión solo si tiene indicador en ella. Solo editable con
 * la VERSIÓN en estado `edicion`.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type Indicador from '@/interfaces/Indicador'
import { http } from '@/services/http'

/**
 * Consulta indicadores · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Indicador[]
 */
export async function getIndicadoresService(baseUrl: string): Promise<Indicador[]> {
  const result = await http.get<any>(baseUrl)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? [])
}

/**
 * Consulta indicadores · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Indicador | null
 */
export async function getIndicadorByIdService(baseUrl: string, id: number): Promise<Indicador | null> {
  const result = await http.get<Indicador>(`${baseUrl}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Consulta indicadores · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Indicador[]
 */
export async function getIndicadoresPorComponenteService(baseUrl: string, id_componente: number): Promise<Indicador[]> {
  const result = await http.get<any>(`${baseUrl}/${id_componente}/indicadores`)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? [])
}

/**
 * Crea indicadores.
 */
export async function crearIndicadorService(
  baseUrlComponentes: string,
  id_componente: number,
  payload: {
    id_version: number
    id_tipo_componente: number
    enunciado: string
    formula?: string | null
    unidades?: string | null
    fuente_informacion?: string | null
    descripcion?: string | null
    peso_opinion?: number | null
  }
): Promise<{ ok: boolean; data?: Indicador; message?: string }> {
  const result = await http.post<Indicador>(`${baseUrlComponentes}/${id_componente}/indicadores`, payload)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true, data: result.data }
}

/**
 * Elimina indicadores · petición `DELETE`.
 *
 * @returns { ok: boolean; message?: string }
 */
export async function eliminarIndicadorService(
  baseUrl: string,
  id_indicador: number
): Promise<{ ok: boolean; message?: string }> {
  const result = await http.delete(`${baseUrl}/${id_indicador}`)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true }
}

/**
 * Actualiza indicadores · petición `PUT`.
 *
 * @returns { ok: boolean; data?: Indicador; message?: string }
 */
export async function updateIndicadorService(
  baseUrl: string,
  id_indicador: number,
  payload: Partial<Pick<Indicador, 'enunciado' | 'formula' | 'unidades' | 'fuente_informacion' | 'descripcion' | 'peso_opinion'>>
): Promise<{ ok: boolean; data?: Indicador; message?: string }> {
  const result = await http.put<Indicador>(`${baseUrl}/${id_indicador}`, payload)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true, data: result.data }
}
